// Pure decision logic for the two HTTP-polling loops introduced by TES-265
// (device-count refresh on ProductPage, WeClapp push on BillingPage).
// Extracted from the pages so the gate/transition rules — where the polling
// bugs live — are unit-testable without rendering MUI pages. Structural
// types keep this file dependency-free.

// --- Device-count refresh (ProductPage) ---

export interface RefreshRunSnapshot {
  id: string
  status: string // running | success | failed
  completed: number
  total: number
  failedCustomerIds?: string[]
}

export type RefreshSnapshotAction =
  /** No run has ever happened — nothing to do. */
  | {kind: 'none'}
  /** A run is live (ours or another pod's): follow it and show progress. */
  | {kind: 'follow'; runId: string; completed: number; total: number}
  /** The run THIS page was following has finished: run completion handling once. */
  | {kind: 'complete'; failed: boolean; failedCustomerIds: string[]}
  /** A finished run this page never followed while idle: just ensure the spinner is off. */
  | {kind: 'stop-idle'}
  /** A finished run while we follow a different id (stale poll frame): ignore. */
  | {kind: 'ignore'}

export function classifyRefreshRunSnapshot(
  run: RefreshRunSnapshot | null,
  watchedRunId: string | null,
): RefreshSnapshotAction {
  if (!run) return {kind: 'none'}
  if (run.status === 'running') {
    return {kind: 'follow', runId: run.id, completed: run.completed, total: run.total}
  }
  if (watchedRunId === run.id) {
    return {
      kind: 'complete',
      failed: run.status === 'failed',
      failedCustomerIds: run.failedCustomerIds ?? [],
    }
  }
  if (watchedRunId === null) return {kind: 'stop-idle'}
  return {kind: 'ignore'}
}

// --- WeClapp push (BillingPage) ---

export interface BillingRunSnapshot {
  status: string // pending | approved | concluded | ...
  lineItems: ReadonlyArray<{weClappPushStatus: string}>
}

/** True while the server-side push loop still has rows to move. */
export function isPushInFlight(run: BillingRunSnapshot | null): boolean {
  return run?.status === 'approved'
    && run.lineItems.some(li => li.weClappPushStatus === 'pending' || li.weClappPushStatus === 'pushing')
}

/**
 * Progress of an in-flight push, derived from the polled run instead of the
 * former socket 'billing-push-progress' events: completed is the number of
 * rows already settled (success/failed/skipped), total is all rows in the
 * run. Returns null when no push is in flight — including right after a
 * bulk retry kicks off, where previously-failed rows count as settled until
 * the server flips them to 'pushing'; the bar then reappears showing the
 * retried remainder. Null also hides the bar.
 */
export function computePushProgress(
  run: BillingRunSnapshot | null,
): {completed: number; total: number} | null {
  if (!run || !isPushInFlight(run)) return null
  const settled = run.lineItems.filter(
    li => li.weClappPushStatus !== 'pending' && li.weClappPushStatus !== 'pushing',
  ).length
  return {completed: settled, total: run.lineItems.length}
}

/**
 * Poll gate. The forced deadline exists because the bulk retry endpoint is
 * fire-and-forget: it returns 202 before any row has flipped to 'pushing',
 * so right after a retry the row statuses alone say "nothing in flight" —
 * exactly the state that left the grid stale (TES-265 finding F3).
 */
export function shouldPollBillingRun(
  run: BillingRunSnapshot | null,
  forcePollUntil: number,
  now: number,
): boolean {
  return isPushInFlight(run) || now < forcePollUntil
}
